import { useEffect, useState } from "react";

import Api from "../../../shared/api/api";
import AddUserForm from "./AddUserForm";

const ROLE_FIELDS = [
  "participant",
  "admin",
  "leader",
  "coordinator",
];

const normalizeStoredValue = (value) =>
  value === "N/A" || value == null
    ? ""
    : value;

export default function UsersAdminList({
  users = [],
  setUsers,
  usersActiveModal = false,
  setUsersActiveModal,
}) {
  const [formData, setFormData] = useState({});
  const [isVisible, setIsVisible] =
    useState(false);

  const [updatingId, setUpdatingId] =
    useState("");

  const [deletingId, setDeletingId] =
    useState("");

  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const initialFormData = users.reduce(
      (currentData, user) => {
        currentData[user.four_h_id] = {
          nameFirst:
            normalizeStoredValue(
              user.name_first
            ),
          nameMiddle:
            normalizeStoredValue(
              user.name_middle
            ),
          nameLast:
            normalizeStoredValue(
              user.name_last
            ),
          email:
            normalizeStoredValue(user.email),
          participant:
            Boolean(user.participant),
          admin: Boolean(user.admin),
          leader: Boolean(user.leader),
          coordinator:
            Boolean(user.coordinator),
          team:
            normalizeStoredValue(user.team),
          level:
            normalizeStoredValue(user.level),
        };

        return currentData;
      },
      {}
    );

    setFormData(initialFormData);
  }, [users]);

  const handleInputChange = (
    userId,
    field,
    value
  ) => {
    setFormData((currentData) => {
      if (field === "role") {
        return {
          ...currentData,
          [userId]: {
            ...currentData[userId],
            participant:
              value === "participant",
            admin: value === "admin",
            leader: value === "leader",
            coordinator:
              value === "coordinator",
          },
        };
      }

      return {
        ...currentData,
        [userId]: {
          ...currentData[userId],
          [field]: value,
        },
      };
    });

    setError("");
    setStatus("");
  };

  const getSelectedRole = (userId) =>
    ROLE_FIELDS.find(
      (role) => formData[userId]?.[role]
    ) || "participant";

  const handleUpdate = async (userId) => {
    const userData = formData[userId];

    if (!userData) {
      return;
    }

    setUpdatingId(userId);
    setError("");
    setStatus("");

    const formattedData = {
      name_first: userData.nameFirst.trim(),
      name_middle:
        userData.nameMiddle.trim(),
      name_last: userData.nameLast.trim(),
      email: userData.email.trim(),
      participant: userData.participant,
      admin: userData.admin,
      leader: userData.leader,
      coordinator: userData.coordinator,
      team: userData.team.trim(),
      level: userData.level.trim(),
    };

    try {
      const response = await Api.updateUser(
        userId,
        formattedData
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      if (typeof setUsers === "function") {
        setUsers((currentUsers) =>
          currentUsers.map((user) =>
            user.four_h_id === userId
              ? {
                  ...user,
                  ...formattedData,
                }
              : user
          )
        );
      }

      setStatus(`User ${userId} was updated.`);
    } catch (requestError) {
      console.error(
        "Unable to update FourH user:",
        requestError
      );

      setError(
        requestError?.message ||
          "The user could not be updated."
      );
    } finally {
      setUpdatingId("");
    }
  };

  const handleDelete = async (userId) => {
    const confirmed = window.confirm(
      `Delete user ${userId}?`
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(userId);
    setError("");
    setStatus("");

    try {
      const response = await Api.deleteSingle(
        "users",
        userId,
        ""
      );

      if (response?.error) {
        throw new Error(response.error);
      }

      if (typeof setUsers === "function") {
        setUsers((currentUsers) =>
          currentUsers.filter(
            (user) =>
              user.four_h_id !== userId
          )
        );
      }

      setStatus(`User ${userId} was deleted.`);
    } catch (requestError) {
      console.error(
        "Unable to delete FourH user:",
        requestError
      );

      setError(
        requestError?.message ||
          "The user could not be deleted."
      );
    } finally {
      setDeletingId("");
    }
  };

  const toggleAddUser = () => {
    if (
      typeof setUsersActiveModal === "function"
    ) {
      setUsersActiveModal(
        (currentValue) => !currentValue
      );
    }
  };

  return (
    <section className="usersListDiv">
      <button
        type="button"
        className="adminHeaders adminSectionToggle"
        onClick={() =>
          setIsVisible((currentValue) => !currentValue)
        }
        aria-expanded={isVisible}
        aria-controls="fourh-users-list"
      >
        <span>Users</span>
        <span aria-hidden="true">
          {isVisible ? "−" : "+"}
        </span>
      </button>

      {error && (
        <p
          className="fourHFormStatus error"
          role="alert"
        >
          {error}
        </p>
      )}

      {status && (
        <p
          className="fourHFormStatus success"
          role="status"
        >
          {status}
        </p>
      )}

      {isVisible && (
        <div
          id="fourh-users-list"
          className="listOfUsers"
        >
          {users.length === 0 ? (
            <p className="fourHEmptyMessage">
              No users were found.
            </p>
          ) : (
            <div className="fourHTableWrapper">
              <table className="fourHAdminTable">
                <thead>
                  <tr>
                    <th scope="col">ID</th>
                    <th scope="col">
                      First Name
                    </th>
                    <th scope="col">
                      Middle Name
                    </th>
                    <th scope="col">
                      Last Name
                    </th>
                    <th scope="col">Email</th>
                    <th scope="col">Role</th>
                    <th scope="col">Team</th>
                    <th scope="col">Level</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {users.map((user) => {
                    const userId =
                      user.four_h_id;

                    const isUpdating =
                      updatingId === userId;

                    const isDeleting =
                      deletingId === userId;

                    return (
                      <tr
                        key={userId}
                        className="tableRow"
                      >
                        <td data-label="4-H ID">
                          {userId}
                        </td>

                        <td data-label="First Name">
                          <input
                            type="text"
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.nameFirst || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "nameFirst",
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td data-label="Middle Name">
                          <input
                            type="text"
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.nameMiddle || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "nameMiddle",
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td data-label="Last Name">
                          <input
                            type="text"
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.nameLast || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "nameLast",
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td data-label="Email">
                          <input
                            type="email"
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.email || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "email",
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td data-label="Role">
                          <select
                            className="usersSelect"
                            value={getSelectedRole(
                              userId
                            )}
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "role",
                                event.target.value
                              )
                            }
                          >
                            <option value="participant">
                              Participant
                            </option>
                            <option value="admin">
                              Admin
                            </option>
                            <option value="leader">
                              Leader
                            </option>
                            <option value="coordinator">
                              Coordinator
                            </option>
                          </select>
                        </td>

                        <td data-label="Team">
                          <input
                            type="text"
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.team || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "team",
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td data-label="Level">
                          <select
                            className="userUpdateInputs"
                            value={
                              formData[userId]
                                ?.level || ""
                            }
                            onChange={(event) =>
                              handleInputChange(
                                userId,
                                "level",
                                event.target.value
                              )
                            }
                          >
                            <option value="">
                              Select Level
                            </option>
                            <option value="Junior">
                              Junior
                            </option>
                            <option value="Intermediate">
                              Intermediate
                            </option>
                            <option value="Senior">
                              Senior
                            </option>
                          </select>
                        </td>

                        <td data-label="Actions">
                          <div className="adminTableActions">
                            <button
                              type="button"
                              className="updateUserBtn"
                              onClick={() =>
                                handleUpdate(userId)
                              }
                              disabled={
                                isUpdating ||
                                isDeleting
                              }
                            >
                              {isUpdating
                                ? "Updating..."
                                : "Update User"}
                            </button>

                            <button
                              type="button"
                              className="deleteUserBtn"
                              onClick={() =>
                                handleDelete(userId)
                              }
                              disabled={
                                isUpdating ||
                                isDeleting
                              }
                            >
                              {isDeleting
                                ? "Deleting..."
                                : "Delete User"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {typeof setUsersActiveModal ===
            "function" && (
            <button
              className="addBtn"
              type="button"
              onClick={toggleAddUser}
            >
              {usersActiveModal
                ? "Close Add User"
                : "Add User"}
            </button>
          )}

          {usersActiveModal && (
            <AddUserForm
              users={users}
              setUsers={setUsers}
              onUserAdded={() => {
                if (
                  typeof setUsersActiveModal ===
                  "function"
                ) {
                  setUsersActiveModal(false);
                }
              }}
            />
          )}
        </div>
      )}
    </section>
  );
}